import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { colors, fonts, BOOK_SALE_STAGES } from "../styles/theme";
import { useTenant } from "../contexts/TenantContext";
import { Button } from "./ui";
import { createBookOpportunity, updateBookOpportunity } from "../services/api";

const inputStyle = {
  width: "100%",
  padding: "9px 12px",
  fontSize: 13,
  fontFamily: fonts.body,
  border: `1px solid ${colors.border}`,
  borderRadius: 6,
  background: colors.bg,
  color: colors.text,
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle = {
  display: "block",
  fontSize: 11,
  color: colors.textSecondary,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: 5,
  fontFamily: fonts.body,
  fontWeight: 600,
};

export function BookSaleModal({ opportunity, onClose, onSaved }) {
  const { currencySymbol } = useTenant();
  const isEdit = !!opportunity;
  const [form, setForm] = useState({
    companyName: opportunity?.companyName || "",
    contactPerson: opportunity?.contactPerson || "",
    estimatedQuantity: opportunity?.estimatedQuantity || "",
    estimatedValue: opportunity?.estimatedValue || "",
    probability: opportunity?.probability != null ? String(opportunity.probability) : "",
    status: opportunity?.status || "lead",
    notes: opportunity?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isDirty = form.companyName || form.contactPerson || form.notes;

  const handleClose = () => {
    if (!isEdit && isDirty && !window.confirm("Discard unsaved opportunity?")) return;
    onClose();
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.companyName.trim()) {
      setError("Company name is required");
      return;
    }

    setSaving(true);
    try {
      const data = {
        companyName: form.companyName.trim(),
        contactPerson: form.contactPerson.trim(),
        estimatedQuantity: parseInt(form.estimatedQuantity) || 0,
        estimatedValue: parseFloat(form.estimatedValue) || 0,
        probability: form.probability !== "" ? parseInt(form.probability) : null,
        status: form.status,
        notes: form.notes.trim(),
      };

      if (isEdit) {
        await updateBookOpportunity(opportunity.id, data);
      } else {
        await createBookOpportunity(data);
      }
      onSaved();
    } catch (err) {
      setError(err.message || "Failed to save opportunity");
      setSaving(false);
    }
  };

  return (
    <>
      <div
        onClick={handleClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 99,
        }}
      />
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 100,
          pointerEvents: "none",
        }}
      >
        <form
          onSubmit={handleSubmit}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            padding: 32,
            width: "100%",
            maxWidth: 520,
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            pointerEvents: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 24,
            }}
          >
            <h2
              style={{
                fontFamily: fonts.heading,
                fontSize: 20,
                color: colors.text,
                margin: 0,
              }}
            >
              {isEdit ? "Edit Opportunity" : "Add Opportunity"}
            </h2>
            <X
              size={20}
              color={colors.textSecondary}
              style={{ cursor: "pointer" }}
              onClick={handleClose}
            />
          </div>

          {/* Company Name — full width */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>
              Company Name <span style={{ color: colors.danger }}>*</span>
            </label>
            <input
              type="text"
              value={form.companyName}
              onChange={handleChange("companyName")}
              placeholder="Meridian Capital"
              style={inputStyle}
              autoFocus
            />
          </div>

          {/* Contact Person + Status — 2 columns */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginBottom: 16,
            }}
          >
            <div>
              <label style={labelStyle}>Contact Person</label>
              <input
                type="text"
                value={form.contactPerson}
                onChange={handleChange("contactPerson")}
                placeholder="Sarah Chen"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {BOOK_SALE_STAGES.map((stage) => (
                  <button
                    key={stage.id}
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({ ...prev, status: stage.id }))
                    }
                    style={{
                      flex: 1,
                      padding: "7px 0",
                      borderRadius: 6,
                      border: `1px solid ${form.status === stage.id ? stage.color : colors.border}`,
                      background:
                        form.status === stage.id
                          ? stage.color + "1a"
                          : "transparent",
                      color:
                        form.status === stage.id
                          ? stage.color
                          : colors.textSecondary,
                      fontSize: 10,
                      fontFamily: fonts.body,
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.15s",
                      minWidth: 0,
                    }}
                  >
                    {stage.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Quantity + Value + Probability — 3 columns */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 16,
              marginBottom: 16,
            }}
          >
            <div>
              <label style={labelStyle}>Est. Quantity</label>
              <input
                type="number"
                value={form.estimatedQuantity}
                onChange={handleChange("estimatedQuantity")}
                placeholder="50"
                min="0"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Est. Value ({currencySymbol()})</label>
              <input
                type="number"
                value={form.estimatedValue}
                onChange={handleChange("estimatedValue")}
                placeholder="1250"
                min="0"
                step="0.01"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Probability (%)</label>
              <input
                type="number"
                value={form.probability}
                onChange={handleChange("probability")}
                placeholder={`${BOOK_SALE_STAGES.find((s) => s.id === form.status)?.defaultProbability ?? 0}`}
                min="0"
                max="100"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Notes — full width */}
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Notes</label>
            <textarea
              value={form.notes}
              onChange={handleChange("notes")}
              placeholder="Interested in bulk purchase for team offsite..."
              rows={3}
              style={{
                ...inputStyle,
                resize: "vertical",
                minHeight: 70,
              }}
            />
          </div>

          {error && (
            <div
              style={{
                padding: "10px 14px",
                marginBottom: 16,
                borderRadius: 6,
                background: "rgba(239,68,68,0.1)",
                border: "1px solid rgba(239,68,68,0.3)",
                color: colors.danger,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button variant="default" type="button" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" style={{ minWidth: 100 }}>
              {saving ? (
                <>
                  <Loader2
                    size={14}
                    className="spin"
                  />
                  Saving...
                </>
              ) : isEdit ? (
                "Update"
              ) : (
                "Add Opportunity"
              )}
            </Button>
          </div>
        </form>
      </div>    </>
  );
}
